import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  toUtcMidnightFromYMD,
  utcMidnightToday,
  ymd,
} from "../utilities/DateUtilities";

export function useUrlDateRange(defaultDays = 7) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  useEffect(() => {
    const startStr = searchParams.get("startDate");
    const endStr = searchParams.get("endDate");

    const start = startStr ? toUtcMidnightFromYMD(startStr) : null;
    const end = endStr ? toUtcMidnightFromYMD(endStr) : null;

    if (start && end) {
      setStartDate(start);
      setEndDate(end);
      return;
    }

    const today = utcMidnightToday();
    const startDefault = new Date(today);
    startDefault.setUTCDate(today.getUTCDate() - defaultDays);
    setStartDate(startDefault);
    setEndDate(today);

    setSearchParams(
      (prev) => {
        // base params
        const newParams: Record<string, string> = {
          startDate: ymd(startDefault),
          endDate: ymd(today),
        };

        // copy through existing params except the two we're replacing
        prev.forEach((value, key) => {
          if (key !== "startDate" && key !== "endDate" && value !== null) {
            newParams[key] = value;
          }
        });

        return newParams;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams, defaultDays]);

  const setRange = useCallback(
    (start: Date, end: Date, replace = true) => {
      setStartDate(start);
      setEndDate(end);

      setSearchParams(
        (prev) => {
          // base params
          const newParams: Record<string, string> = {
            startDate: ymd(start),
            endDate: ymd(end),
          };

          // copy through existing params except the two we're replacing
          prev.forEach((value, key) => {
            if (key !== "startDate" && key !== "endDate" && value !== null) {
              newParams[key] = value;
            }
          });

          return newParams;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  return { startDate, endDate, setRange };
}
